import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPoll,
    faBell,
    faArrowRight,
    faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function FeedbackIndex() {
    const sections = [
        {
            id: 'encuestas',
            title: 'Encuestas',
            icon: faPoll,
            description: 'Crea y gestiona encuestas de satisfacción para recopilar feedback de tus clientes',
            path: '/documentation/feedback/encuestas',
            status: 'disponible',
            features: [
                'Crear encuestas con preguntas predefinidas de satisfacción',
                'Generar códigos QR para compartir fácilmente',
                'Descargar PDFs con códigos QR para imprimir',
                'Compartir enlaces directos de encuestas',
                'Activar y desactivar encuestas',
                'Ver resultados y análisis de respuestas'
            ]
        },
        {
            id: 'alertas',
            title: 'Alertas',
            icon: faBell,
            description: 'Gestiona alertas automáticas sobre eventos importantes de tu negocio',
            path: '/documentation/feedback/alertas',
            status: 'disponible',
            features: [
                'Alertas de cumpleaños de clientes',
                'Alertas de fechas clave y eventos',
                'Alertas de objetivos alcanzados',
                'Marcar alertas como leídas',
                'Filtrar por tipo de alerta',
                'Enviar recompensas directamente desde las alertas'
            ]
        }
    ];

    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-6">
                        <FontAwesomeIcon icon={faPoll} className="text-purple-600 text-4xl" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Feedback y Alertas
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Recopila feedback de tus clientes y mantente al día con alertas importantes sobre eventos y objetivos de tu negocio.
                    </p>
                </div>

                {/* Introducción */}
                <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-lg mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3">
                        ¿Qué es Feedback y Alertas?
                    </h2>
                    <p className="text-gray-700 mb-4">
                        El módulo de Feedback y Alertas te ayuda a:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li><strong>Recopilar Feedback:</strong> Crea encuestas de satisfacción para conocer la opinión de tus clientes y mejorar tu servicio</li>
                        <li><strong>Mantenerte Informado:</strong> Recibe alertas automáticas sobre eventos importantes como cumpleaños de clientes, fechas clave y objetivos alcanzados</li>
                        <li><strong>Tomar Acción:</strong> Actúa rápidamente sobre las alertas enviando recompensas o tomando decisiones basadas en el feedback recibido</li>
                    </ul>
                </div>

                {/* Secciones */}
                <div className="space-y-6 mb-8">
                    <h2 className="text-3xl font-bold text-gray-800 mb-6">
                        Secciones Disponibles
                    </h2>
                    {sections.map((section) => (
                        <div
                            key={section.id}
                            className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden"
                        >
                            <div className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-start space-x-4 flex-1">
                                        <div className="flex-shrink-0">
                                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                                section.status === 'disponible' 
                                                    ? 'bg-purple-100' 
                                                    : 'bg-gray-100'
                                            }`}>
                                                <FontAwesomeIcon 
                                                    icon={section.icon} 
                                                    className={`text-xl ${
                                                        section.status === 'disponible' 
                                                            ? 'text-purple-600' 
                                                            : 'text-gray-400'
                                                    }`} 
                                                />
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h3 className="text-xl font-semibold text-gray-800">
                                                    {section.title}
                                                </h3>
                                                {section.status === 'disponible' && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                                                        Disponible
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-gray-600 mb-4">
                                                {section.description}
                                            </p>
                                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                                                {section.features.map((feature, index) => (
                                                    <li key={index} className="flex items-start text-sm text-gray-700">
                                                        <span className="text-purple-500 mr-2">•</span>
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                {section.status === 'disponible' && (
                                    <Link
                                        to={section.path}
                                        className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium transition-colors"
                                    >
                                        Ver documentación completa
                                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                                    </Link>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Tips */}
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        💡 Consejos para aprovechar Feedback y Alertas
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li>Usa las encuestas regularmente para medir la satisfacción de tus clientes y identificar áreas de mejora</li>
                        <li>Coloca códigos QR de encuestas en lugares visibles de tu tienda para facilitar la participación</li>
                        <li>Revisa las alertas diariamente para no perder oportunidades de conectar con tus clientes</li>
                        <li>Aprovecha las alertas de cumpleaños para enviar recompensas especiales y fortalecer la relación con tus clientes</li>
                        <li>Analiza los resultados de las encuestas para tomar decisiones informadas sobre mejoras en tu negocio</li>
                    </ul>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default FeedbackIndex;

